import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import LgmButton from '../LgmButton';
import { useApi } from '../../services/api/provider';
import { ProductService } from '../../services/api/domains/products/service';
import {
  StagedExternalLinkRecord,
  CreateExternalLinksResponse,
  CreateExternalLinksValidationFailedBody,
  CreateExternalLinksBadRequestBody,
} from '../../services/api/domains/products/types';
import { getStagedRecordKey } from '../../domains/products/adapters';
import { ApiError } from '../../services/api/types';
import { notifyValidationJobsPoll } from '../../shared/hooks/validationJobsPollBus';
import { GRAY_900 } from 'theme/grayScale';
import {
  COLOR_DANGER_TEXT,
  COLOR_WARNING_TEXT,
  COLOR_WARNING_BG,
} from 'theme/colors';

// ---------------------------------------------------------------------------
// LinkConfirmDialog — issue #622, Sub-task 9.5
// ---------------------------------------------------------------------------

type DialogPhase = 'confirming' | 'linking' | 'link-error' | 'linked';

/**
 * Maps a non-validation `ApiError.code` to an honest, retryable-vs-not
 * message (Requirement 15, docs/features/external-source-linking.md:419-423
 * — "a stale session or changed role must not produce an unhandled error"
 * and "retryable vs. non-retryable distinguished in the message"). Every
 * `ApiClient` error carries `.code` (client.ts's own createApiError), but
 * previously this dialog only ever showed `apiErr.message`, which for
 * FORBIDDEN/UNAUTHORIZED/UNKNOWN_ERROR is the generic string `createApiError`
 * sets ('Forbidden'/'Unauthorized'/'An unexpected error occurred') — never
 * distinguishing "retry won't help until your access changes" (403/401)
 * from "this may well succeed if you try again" (network/5xx).
 *
 * @param {ApiError} apiErr
 * @returns {string}
 */
function describeLinkError(apiErr: ApiError): string {
  switch (apiErr.code) {
    case 'FORBIDDEN':
      return "You don't have permission to create this link. Your role may have changed — refresh the page and, if the problem continues, contact an admin. Retrying without refreshing won't help.";
    case 'UNAUTHORIZED':
      return 'Your session has expired. Refresh the page and sign in again, then retry.';
    case 'NETWORK_ERROR':
      // Already the specific, actionable timeout message from client.ts.
      return apiErr.message;
    case 'UNKNOWN_ERROR':
      return `${apiErr.message || 'An unexpected error occurred'}. This may be temporary — try again.`;
    default:
      return apiErr.message || 'Failed to create the link(s). Try again.';
  }
}

export interface LinkConfirmDialogProps {
  productId: number;
  sourceLabel: string;
  records: StagedExternalLinkRecord[];
  /** Backdrop click / Escape / "Back to review" — never commits. Returns to the review list, keeping every staged record intact. */
  onClose: () => void;
  /** Only reached after a successful commit. */
  onLinked: (response: CreateExternalLinksResponse) => void;
}

/**
 * Confirmation dialog for committing the review list's staged records
 * (Sub-task 9.5). Lists every record about to be linked and calls
 * `ProductService.createExternalLinks` (Task 6.2) on confirm.
 *
 * **Open Question 35 / tombstone-resurrection gap** — the acceptance
 * criteria call for naming WHEN and BY WHOM a reversed rejection happened.
 * That is not achievable honestly with the current API surface:
 *
 *   - `dbo.External_Link_Rejections` DOES carry `created_by`/`created_date`
 *     (the original rejection's who/when) — see
 *     `sql/migrations/V1_3_60__create_external_link_rejections.sql` — but
 *     no Task 6/7 endpoint returns those columns anywhere. The create-link
 *     response's `linked[].tombstone_cleared` is a bare boolean, known only
 *     AFTER commit, not before.
 *   - Suggestion-added records (`addedVia: 'suggestion'`) can never be
 *     tombstoned in the first place —
 *     `fetchSuggestionCandidatePool`/`buildTombstoneExclusionSql`
 *     (api/sql/externalLinkSearch.js) exclude actively-tombstoned records
 *     from the candidate pool BEFORE scoring, so this only matters for
 *     search-added records at all.
 *   - Search's type-ahead endpoint (7.1) does no tombstone exclusion and
 *     returns no tombstone flag/metadata on any result row, so there is no
 *     signal available here to check pre-commit.
 *
 * Rather than fabricate a "who/when" or silently drop the requirement, this
 * dialog shows an honest, generic disclaimer (only when at least one
 * search-added record is present, since that's the only case where it's
 * even possible) explaining that a reversal may happen and that who/when
 * isn't available — see this component's session report for the follow-up
 * needed on the API side (surfacing `created_by`/`created_date` on a
 * would-be-cleared tombstone) to fully satisfy Open Question 35.
 */
const LinkConfirmDialog: React.FC<LinkConfirmDialogProps> = ({
  productId,
  sourceLabel,
  records,
  onClose,
  onLinked,
}) => {
  const api = useApi();
  const productService = React.useMemo(() => new ProductService(api), [api]);

  const [phase, setPhase] = React.useState<DialogPhase>('confirming');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  // Holds the response through the 'linked' summary phase below -- issue
  // #622 exhaustive-review follow-up: a record that comes back in
  // `already_linked` (e.g. it was linked by someone/something else between
  // staging and confirming) used to be completely indistinguishable from a
  // full success -- the dialog just closed with zero visible difference.
  const [linkResult, setLinkResult] =
    React.useState<CreateExternalLinksResponse | null>(null);

  const isLinking = phase === 'linking';
  const hasSearchAddedRecord = records.some(r => r.addedVia === 'search');

  const handleConfirm = async () => {
    setPhase('linking');
    setErrorMessage(null);
    try {
      const response = await productService.createExternalLinks(
        productId,
        records.map(r => ({ source: r.source, record_id: r.record_id }))
      );
      setLinkResult(response.data);
      setPhase('linked');
      // externalLinks.js auto-queues a validation job on a new link (issue
      // #637, Seq 8) -- nudge the nav indicator to poll immediately instead
      // of waiting up to its own 15s interval, same as the devtool modal's
      // Run button. Without this, a fast job (a handful of steps) can start
      // and finish entirely between two indicator polls, making it look
      // like nothing was triggered at all.
      notifyValidationJobsPoll();
    } catch (err) {
      const apiErr = err as ApiError;
      const body = apiErr.response?.data as
        | CreateExternalLinksValidationFailedBody
        | CreateExternalLinksBadRequestBody
        | undefined;

      if (body?.error === 'validation_failed' && body.invalid_records?.length) {
        // Task 6.2's pre-validation contract (docs/API.md) — names every
        // offending record by source + record_id + reason, verbatim, rather
        // than flattening to a generic "something went wrong".
        setErrorMessage(
          `${body.message} ${body.invalid_records
            .map(r => `[${r.source} record ${r.record_id}: ${r.reason}]`)
            .join(' ')}`
        );
      } else if (body?.error === 'validation_error' && body.record) {
        setErrorMessage(
          `${body.message} (${body.record.source} record ${body.record.record_id})`
        );
      } else {
        setErrorMessage(
          apiErr instanceof Error
            ? describeLinkError(apiErr)
            : 'Failed to create the link(s). Try again.'
        );
      }
      setPhase('link-error');
    }
  };

  const handleDone = () => {
    if (linkResult) onLinked(linkResult);
  };

  if (phase === 'linked' && linkResult) {
    const linkedKeys = new Set(
      linkResult.linked.map(r => getStagedRecordKey(r))
    );
    const alreadyLinkedKeys = new Set(
      linkResult.already_linked.map(r => getStagedRecordKey(r))
    );
    const summary =
      linkResult.already_linked.length === 0
        ? `${linkResult.linked.length} record${linkResult.linked.length === 1 ? '' : 's'} linked to ${sourceLabel}`
        : `${linkResult.linked.length} linked, ${linkResult.already_linked.length} already linked to ${sourceLabel}`;

    return (
      <Dialog
        open
        // Deliberately no onClose (PR #719 review follow-up): this summary
        // is the acknowledgement step for a partial-success outcome
        // (design decision -- "N linked, M already linked" must be seen,
        // not silently dismissed). Escape/backdrop-click must NOT call
        // handleDone -- only the explicit "Done" button below may.
        sx={{ zIndex: 20000 }}
        slotProps={{ paper: { sx: { minWidth: 440, maxWidth: 560 } } }}
        aria-labelledby="link-confirm-dialog-title">
        <DialogTitle
          id="link-confirm-dialog-title"
          sx={{ fontWeight: 600, fontSize: '1rem' }}>
          {summary}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <ul
            style={{ margin: 0, paddingLeft: '20px' }}
            aria-label={`${sourceLabel} link results`}>
            {records.map(r => {
              const key = getStagedRecordKey(r);
              const status = linkedKeys.has(key)
                ? 'Linked'
                : alreadyLinkedKeys.has(key)
                  ? 'Already linked'
                  : null;
              return (
                <li key={key}>
                  <Typography variant="body2" sx={{ color: GRAY_900 }}>
                    {r.record.matched_value}
                    {status && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color:
                            status === 'Already linked'
                              ? COLOR_WARNING_TEXT
                              : GRAY_900,
                        }}>
                        — {status}
                      </span>
                    )}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </DialogContent>
        <DialogActions>
          <LgmButton variant="contained" onClick={handleDone}>
            Done
          </LgmButton>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={isLinking ? undefined : onClose}
      sx={{ zIndex: 20000 }}
      slotProps={{ paper: { sx: { minWidth: 440, maxWidth: 560 } } }}
      aria-labelledby="link-confirm-dialog-title">
      <DialogTitle
        id="link-confirm-dialog-title"
        sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Link {records.length} record{records.length === 1 ? '' : 's'} to{' '}
        {sourceLabel}?
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <ul
          style={{ margin: 0, paddingLeft: '20px' }}
          aria-label={`${sourceLabel} records to be linked`}>
          {records.map(r => (
            <li key={getStagedRecordKey(r)}>
              <Typography variant="body2" sx={{ color: GRAY_900 }}>
                {r.record.matched_value}
              </Typography>
            </li>
          ))}
        </ul>

        {hasSearchAddedRecord && (
          <div
            style={{
              background: COLOR_WARNING_BG,
              border: '1px solid #fde68a',
              borderRadius: '6px',
              padding: '8px 12px',
            }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: COLOR_WARNING_TEXT }}>
              A previously-rejected record may be resurrected
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: COLOR_WARNING_TEXT, mt: 0.25 }}>
              If any of these records were previously unlinked (rejected) for
              this product, linking them now reverses that rejection. Who made
              the original rejection and when isn&apos;t shown here — the
              current API doesn&apos;t return that yet (see this feature&apos;s
              session report for the follow-up needed).
            </Typography>
          </div>
        )}

        {phase === 'link-error' && errorMessage && (
          <Typography
            variant="body2"
            sx={{ color: COLOR_DANGER_TEXT }}
            role="alert">
            {errorMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <LgmButton variant="outlined" onClick={onClose} disabled={isLinking}>
          Back to review
        </LgmButton>
        <LgmButton
          variant="contained"
          onClick={handleConfirm}
          disabled={isLinking || records.length === 0}>
          {isLinking ? 'Linking…' : 'Link'}
        </LgmButton>
      </DialogActions>
    </Dialog>
  );
};

export default LinkConfirmDialog;
